import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Suitcase } from '@factorialco/f0-react/icons/app'
import {
  MyRequestsDataCollectionDocument,
  MyRequestsDataCollectionQuery,
  MyRequestsEmployeesFilterDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'

import { buildMapVariables } from './mapVariables'
import { getStatusConfig } from './statusUtils'
import { useActions } from './useActions'

type RequestNode = NonNullable<
  MyRequestsDataCollectionQuery['trainings']['trainingRequestsConnection']['nodes']
>[number]

interface MyRequestsDataCollectionProps {
  canCreate: boolean
}

export const MyRequestsDataCollection = ({ canCreate }: MyRequestsDataCollectionProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const client = useApolloClient()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { itemActions } = useActions()

  const source = useGraphqlDataCollectionSource({
    document: MyRequestsDataCollectionDocument,
    mapData: (data) => data.trainings.trainingRequestsConnection.nodes,
    mapPagination: (data) => ({
      total: data.trainings.trainingRequestsConnection.totalCount ?? 0,
    }),
    mapVariables: buildMapVariables(companyId),
    itemOnClick: (item: RequestNode) => () => {
      track(trackingEvents.training.trainingRequest.goToTrainingRequest, {
        from: 'my_requests',
      })
      navigate({ hash: encodeActivity('trainings.myTrainingRequest', { id: String(item.id) }) })
    },
    filters: {
      applicant: {
        label: i18n.t('trainings.training_requests.my_requests.datatable.columns.applicant'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: MyRequestsEmployeesFilterDocument,
              fetchPolicy: 'cache-first',
            })

            const uniqueAuthors = new Map<string, { label: string; value: string }>()
            result.data?.trainings.trainingRequestsConnection.nodes.forEach((node) => {
              if (node.author && !uniqueAuthors.has(node.author.id)) {
                uniqueAuthors.set(node.author.id, {
                  label: node.author.fullName,
                  value: node.author.id,
                })
              }
            })

            return Array.from(uniqueAuthors.values())
          },
        },
      },
      status: {
        label: i18n.t('trainings.training_requests.my_requests.datatable.columns.status'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('trainings.training_requests.my_requests.datatable.status_tag.draft'),
              value: 'draft',
            },
            {
              label: i18n.t(
                'trainings.training_requests.my_requests.datatable.status_tag.pending'
              ),
              value: 'review',
            },
            {
              label: i18n.t(
                'trainings.training_requests.my_requests.datatable.status_tag.approved'
              ),
              value: 'approved',
            },
            {
              label: i18n.t(
                'trainings.training_requests.my_requests.datatable.status_tag.rejected'
              ),
              value: 'rejected',
            },
          ],
        },
      },
    },
    sortings: {
      createdAt: {
        label: i18n.t('trainings.training_requests.my_requests.datatable.columns.request_date'),
      },
    },
    defaultSortings: {
      field: 'createdAt',
      order: 'desc',
    },
    search: {
      enabled: true,
    },
    primaryActions: () =>
      canCreate
        ? {
            label: i18n.t('trainings.training_requests.create.button.from_catalog'),
            icon: Suitcase,
            onClick: () => {
              navigate(navigation.myTrainings.catalog.list.fullPath())
              track(trackingEvents.training.trainingRequest.addTrainingRequest.fromCatalog.start, {
                from: 'my_requests',
              })
            },
          }
        : undefined,
    secondaryActions: {
      expanded: 1,
      actions: () =>
        canCreate
          ? [
              {
                label: i18n.t('trainings.training_requests.create.button.new'),
                icon: Add,
                onClick: () => {
                  navigate(navigation.myTrainings.requests.list.new.fullPath())
                  track(
                    trackingEvents.training.trainingRequest.addTrainingRequest.newProposal.start,
                    {
                      from: 'my_requests',
                    }
                  )
                },
              },
            ]
          : [],
    },
    itemActions,
  })

  return (
    <OneDataCollection
      id="trainings/my_requests/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t(
                  'trainings.training_requests.my_requests.datatable.columns.training_name'
                ),
                render: (item) => ({
                  type: 'text',
                  value: item.trainingName ?? '',
                }),
              },
              {
                label: i18n.t(
                  'trainings.training_requests.my_requests.datatable.columns.competencies'
                ),
                render: (item) => {
                  if (!item.competencies || item.competencies.length === 0) {
                    return { type: 'text', value: '-' }
                  }
                  return {
                    type: 'tagList',
                    value: {
                      type: 'raw',
                      tags: item.competencies.map((c) => ({
                        text: c.name,
                      })),
                      max: 3,
                    },
                  }
                },
              },
              {
                label: i18n.t(
                  'trainings.training_requests.my_requests.datatable.columns.request_date'
                ),
                sorting: 'createdAt',
                render: (item) => {
                  const date = moment(item.createdAt)
                  return {
                    type: 'text',
                    value: dateToString(date, 'ui'),
                  }
                },
              },
              {
                label: i18n.t(
                  'trainings.training_requests.my_requests.datatable.columns.applicant'
                ),
                render: (item) => ({
                  type: 'person',
                  value: {
                    firstName: item.author?.fullName ?? '',
                    lastName: '',
                    avatar: item.author?.avatar ? buildUrl(item.author.avatar) : undefined,
                  },
                }),
              },
              {
                label: i18n.t(
                  'trainings.training_requests.my_requests.datatable.columns.status'
                ),
                render: (item) => {
                  const config = getStatusConfig(item.status)
                  return {
                    type: 'status',
                    value: {
                      label: config.label,
                      status: config.status,
                    },
                  }
                },
              },
            ],
            frozenColumns: 1,
          },
        },
      ]}
    />
  )
}
